'use client'

import { useContext, useEffect, useState } from 'react'
import QuantitySelector from '@dustin-web/microsite-components/src/quantity-selector'
import AddToCartButton from '@dustin-web/product-components/src/add-to-cart-button'
import useShoppingCart from '@dustin-web/product-components/src/hooks/dataloader/use-shopping-cart'
import { ProductLifeCycleState } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import type { ProductPageType } from '@productsite-beta/src/components/product-page-v2'
import type { CartBaseProduct } from '@dustin-web/product-components/src/hooks/cart/cart-base-product-types'
import type { DigitalDataOrigin } from '@dustin-web/product-components/src/utils/digital-data-types'
import { AttachedSalesContext } from '../../../product-context/attached-sales-provider'
import { useIsNavigating } from '../../../product-context/product-optimistic-context'

import './product-add-to-cart.scss'

type Props = {
  product: ProductPageType & Partial<{ categoryPathEng?: string }>
  ticket: string
  disabled?: boolean
  hideQuantitySelector?: boolean
  digitalDataOrigin?: DigitalDataOrigin
}

const ProductAddToCart = ({
  product,
  ticket,
  disabled = false,
  hideQuantitySelector = false,
  digitalDataOrigin = 'productPage',
}: Props) => {
  const minQuantity =
    product.minQuantityPerOrder && product.minQuantityPerOrder > 0 ? product.minQuantityPerOrder : 1
  const [buyQuantity, setBuyQuantity] = useState(minQuantity)
  const { quantityOfProductInCart } = useShoppingCart()
  const { productsToAddToCart, setMainProductQuantity } = useContext(AttachedSalesContext)
  const isNavigating = useIsNavigating()

  let maxQuantity =
    product.maxQuantityPerOrder && product.maxQuantityPerOrder > 0
      ? product.maxQuantityPerOrder
      : 2147483647

  useEffect(() => {
    if (setMainProductQuantity) {
      setMainProductQuantity(buyQuantity)
    }
  }, [buyQuantity, setMainProductQuantity])

  if (!product.availability?.isAvailableForSale) {
    return null
  }

  if (
    product.availability?.productLifeCycleState === ProductLifeCycleState.Clearance ||
    product.availability?.productLifeCycleState === ProductLifeCycleState.ExternalClearance
  ) {
    const cartProduct = product as CartBaseProduct
    if (cartProduct.availability?.maxAvailableQuantity) {
      const quantityInCart = quantityOfProductInCart(product.productErpIdentifier)
      let possibleToAdd = cartProduct.availability.maxAvailableQuantity - quantityInCart
      possibleToAdd = possibleToAdd > 0 ? possibleToAdd : 1

      maxQuantity = possibleToAdd < maxQuantity ? possibleToAdd : maxQuantity
    }
  }

  const isInsurance = product.ecoResCategoryName?.toLowerCase()?.includes('insurances')

  return (
    <form onSubmit={e => e.preventDefault()} className="product-add-to-cart">
      {!hideQuantitySelector && (
        <div className="product-add-to-cart--quantity-selector">
          <QuantitySelector
            disabled={isNavigating}
            productName={product.displayName}
            quantity={buyQuantity}
            setQuantity={i => setBuyQuantity(i)}
            maxQuantity={maxQuantity}
            minQuantity={minQuantity}
          />
        </div>
      )}
      <AddToCartButton
        product={product}
        ticket={ticket}
        isAd={false}
        quantity={buyQuantity}
        mainProductErpIdentifier={null}
        digitalDataOrigin={digitalDataOrigin}
        disabled={disabled || isInsurance || isNavigating}
        className="u-flex-1"
        buttonClassName="product-add-to-cart--button"
        shoppingCartIcon={true}
        hideNotifyMeStatus
        multipleProducts={productsToAddToCart}
        minWidth
      />
    </form>
  )
}

export default ProductAddToCart
